import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import './App.css';
import LoadingIndicator from './LoadingIndicator';
import AppSidebar from './AppSidebar';
import toast from 'react-hot-toast';
import { Toaster } from 'react-hot-toast';
// Helper to easily get YYYY-MM-DD for today
function getTodayString() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

const WEEKDAY_OPTIONS = [
  { value: '1', label: 'Monday' },
  { value: '2', label: 'Tuesday' },
  { value: '3', label: 'Wednesday' },
  { value: '4', label: 'Thursday' },
  { value: '5', label: 'Friday' },
  { value: '6', label: 'Saturday' },
];

const WEEKDAY_LABELS = WEEKDAY_OPTIONS.reduce((labels, item) => {
  labels[item.value] = item.label;
  return labels;
}, {});

function parseDateInput(value) {
  if (!value) return null;
  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return null;
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
}

function formatLocalDate(date) {
  if (!date) return '';
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

function getWeekdayPatternDates(anchorDate, selectedWeekdays = []) {
  const anchor = parseDateInput(anchorDate);
  if (!anchor) return [];

  const weekStart = new Date(anchor);
  const day = weekStart.getDay();
  const mondayOffset = day === 0 ? -6 : 1 - day;
  weekStart.setDate(anchor.getDate() + mondayOffset);

  return [...new Set(selectedWeekdays.map(String))]
    .map((weekday) => Number(weekday))
    .filter((weekday) => Number.isInteger(weekday) && weekday >= 0 && weekday <= 6)
    .sort((a, b) => {
      const left = a === 0 ? 7 : a;
      const right = b === 0 ? 7 : b;
      return left - right;
    })
    .map((weekday) => {
      const result = new Date(weekStart);
      result.setDate(weekStart.getDate() + (weekday === 0 ? 6 : weekday - 1));
      return result;
    });
}

function getWeekOfMonth(date) {
  return Math.floor((date.getDate() - 1) / 7) + 1;
}

function buildWeekdayPatternFrequency(frequency, plannedDate) {
  const normalized = String(frequency || '').trim().toLowerCase();
  const date = parseDateInput(plannedDate);
  const dayName = date ? WEEKDAY_LABELS[String(date.getDay())] : '';

  if (normalized === 'one_time') return 'One Time';
  if (normalized === 'weekly' && dayName) return `Weekly on ${dayName}`;
  if (normalized === 'monthly' && dayName && date) return `Monthly on Week ${getWeekOfMonth(date)} ${dayName}`;
  return '';
}

function buildChecklistFrequency(form) {
  const dateType = form.dateType || 'regular';

  if (dateType === 'skipDays') {
    const skipDays = Number(form.skipDays);
    if (!Number.isInteger(skipDays) || skipDays < 1) return '';
    return `Every ${skipDays + 1} Days`;
  }

  if (dateType === 'dailyRange') {
    return form.recurrenceEndDate ? `Daily Until ${form.recurrenceEndDate}` : '';
  }

  if (dateType === 'weekdays') {
    return buildWeekdayPatternFrequency(form.weekdayFrequency, form.plannedDate);
  }

  return form.frequency;
}

function parseChecklistFrequency(frequency) {
  const raw = String(frequency || '').trim();
  const everyMatch = raw.match(/^Every\s+(\d+)\s+Days?$/i);
  if (everyMatch) {
    const intervalDays = Number(everyMatch[1]);
    return {
      dateType: 'skipDays',
      frequency: '',
      skipDays: Math.max(intervalDays - 1, 1),
      recurrenceEndDate: '',
      weekdayFrequency: 'one_time',
      selectedWeekdays: [],
    };
  }

  const dailyUntilMatch = raw.match(/^Daily\s+Until\s+(\d{4}-\d{2}-\d{2})$/i);
  if (dailyUntilMatch) {
    return {
      dateType: 'dailyRange',
      frequency: '',
      skipDays: 1,
      recurrenceEndDate: dailyUntilMatch[1],
      weekdayFrequency: 'one_time',
      selectedWeekdays: [],
    };
  }

  return {
    dateType: 'regular',
    frequency: raw,
    skipDays: 1,
    recurrenceEndDate: '',
    weekdayFrequency: 'one_time',
    selectedWeekdays: [],
  };
}

function isRecurringFrequencyLabel(frequency) {
  const value = String(frequency || '').trim().toLowerCase();
  return Boolean(value && value !== 'one time');
}

function FancyDropdown({
  label,
  value,
  options,
  placeholder = 'Select',
  loading = false,
  disabled = false,
  error = '',
  searchable = false,
  onChange,
}) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);
  const [searchValue, setSearchValue] = useState('');
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  useEffect(() => {
  if (!open) {
    setSearchValue('');
  }
}, [open]);

  const selected = options.find((item) => String(item.value) === String(value));
  const filteredOptions = options.filter((item) =>
  item.label.toLowerCase().includes(searchValue.toLowerCase())
);
  return (
    <div className="fd-field" ref={wrapperRef}>
      <label className="fd-label">{label}</label>

      <button
        type="button"
        className={`fd-trigger ${open ? 'open' : ''}`}
        onClick={() => !disabled && setOpen((prev) => !prev)}
        disabled={disabled}
      >
        <span className={`fd-value ${!selected ? 'placeholder' : ''}`}>
          {loading ? (
            <LoadingIndicator variant="tiny" label={`Loading ${label.toLowerCase()}...`} />
          ) : (
            selected?.label || placeholder
          )}
        </span>
        <span className="fd-caret" aria-hidden="true" />
      </button>

      {error ? <div className="fd-error">{error}</div> : null}

      {open ? (
  <div className="fd-menu">
    {searchable ? (
      <div className="fd-search">
        <input
          type="text"
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          placeholder={`Search ${label.toLowerCase()}...`}
          autoFocus
        />
      </div>
    ) : null}
    {filteredOptions.length > 0 ? (
      filteredOptions.map((item) => (
        <button
          key={item.value}
          type="button"
          className={`fd-option ${String(value) === String(item.value) ? 'selected' : ''}`}
          onClick={() => {
            onChange(item.value);
            setOpen(false);
          }}
        >
          {item.label}
        </button>
      ))
    ) : (
      <div className="fd-option no-results">No results found</div>
    )}
  </div>
) : null}
    </div>
  );
}

function parseDateOnly(value) {
  if (!value) return null;

  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [y, m, d] = value.split('-').map(Number);
    return new Date(Date.UTC(y, m - 1, d));
  }

  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return null;
  return dt;
}

function isOverdueTask(task) {
  if (!task) return false;
  if (String(task.status || '').toLowerCase() === 'completed') return false;

  const planned = parseDateOnly(task.plannedDate || task.due);
  if (!planned) return false;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const plannedDay = new Date(planned);
  plannedDay.setHours(0, 0, 0, 0);

  return plannedDay < today;
}

export function getInitials(user) {  
  const source = user?.emp_name || user?.username || 'User';
  return source
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('');
}

function getStatusClass(status) {
  const lowered = String(status || '').toLowerCase();

  if (lowered.includes('complete') || lowered.includes('done')) return 'completed';
  if (lowered.includes('progress')) return 'medium';
  if (lowered.includes('review')) return 'high';
  return 'low';
}

function formatDateStr(input) {
  if (!input) return '';

  if (/^\d{4}-\d{2}-\d{2}$/.test(input)) {
    const [y, m, d] = input.split('-').map(Number);
    const dt = new Date(Date.UTC(y, m - 1, d));
    const day = String(dt.getUTCDate()).padStart(2, '0');
    const mon = dt.toLocaleString('en-US', { month: 'short', timeZone: 'UTC' });
    const yy = String(dt.getUTCFullYear()).slice(-2);
    return `${day}-${mon}-${yy}`;
  }

  const dt = new Date(input);
  if (isNaN(dt)) return input;
  const day = String(dt.getDate()).padStart(2, '0');
  const mon = dt.toLocaleString('en-US', { month: 'short' });
  const yy = String(dt.getFullYear()).slice(-2);
  return `${day}-${mon}-${yy}`;
}

function QuickInfoIcon({ type }) {
  const common = {
    width: 14,
    height: 14,
    viewBox: '0 0 24 24',
    fill: 'none',
    xmlns: 'http://www.w3.org/2000/svg',
    'aria-hidden': 'true',
  };

  switch (type) {
    case 'user':
      return (
        <svg {...common}>
          <path d="M20 21a8 8 0 10-16 0" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          <circle cx="12" cy="8" r="4" stroke="currentColor" strokeWidth="1.8" />
        </svg>
      );
    case 'department':
      return (
        <svg {...common}>
          <path d="M4 21h16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          <path d="M6 21V9l6-4 6 4v12" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
          <path d="M10 21v-6h4v6" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
        </svg>
      );
    case 'calendar':
      return (
        <svg {...common}>
          <rect x="4" y="5" width="16" height="15" rx="3" stroke="currentColor" strokeWidth="1.8" />
          <path d="M4 9h16" stroke="currentColor" strokeWidth="1.8" />
          <path d="M8 3v4M16 3v4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
      );
    case 'status':
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="8" stroke="currentColor" strokeWidth="1.8" />
          <path d="M9.5 12.2l1.9 1.9 3.6-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case 'frequency':
      return (
        <svg {...common}>
          <path d="M7 7h4V3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M17 17h-4v4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M7 17a8 8 0 1010-10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
      );
    default:
      return null;
  }
}

function LogoMark() {
  return (
    <svg width="90" height="18" viewBox="0 0 1046 120" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M30 120H5a5 5 0 0 1-5-5V5a5 5 0 0 1 5-5h25a5 5 0 0 1 5 5v37.5a5 5 0 0 0 5 5h4.795a20 20 0 0 0 13.418-5.17l43.93-39.745A10 10 0 0 1 108.852 0h39.572c2.299 0 3.38 2.841 1.661 4.369L89.602 58.132a2.5 2.5 0 0 0 0 3.736l60.483 53.763c1.719 1.528.638 4.369-1.661 4.369h-39.572a10 10 0 0 1-6.709-2.585l-43.93-39.746A20 20 0 0 0 44.795 72.5H40a5 5 0 0 0-5 5V115a5 5 0 0 1-5 5m181.361 0h-27.929c-1.802 0-3.013-1.85-2.29-3.502L230.799 2.996A5 5 0 0 1 235.38 0h38.46a5 5 0 0 1 4.581 2.996l49.657 113.502c.722 1.652-.488 3.502-2.291 3.502h-27.928a5 5 0 0 1-4.569-2.969l-34.111-76.75c-1.76-3.96-7.379-3.96-9.138 0l-34.111 76.75a5 5 0 0 1-4.569 2.969" fill="#1f1f1f" />
      <path fillRule="evenodd" clipRule="evenodd" d="M363.29 0h111.615c16.568 0 30 13.432 30 30v22.814c0 12.988-8.961 24.259-21.615 27.186l24.808 36.084c1.14 1.658-.048 3.916-2.061 3.916h-30.174c-1.615 0-3.13-.78-4.069-2.094l-17.522-24.53A20 20 0 0 0 437.998 85H398.29a5 5 0 0 0-5 5v25a5 5 0 0 1-5 5h-25a5 5 0 0 1-5-5V5a5 5 0 0 1 5-5m35 25h65c5.523 0 10 4.477 10 10v15c0 5.523-4.477 10-10 10h-65a5 5 0 0 1-5-5V30a5 5 0 0 1 5-5M653.542 0h-110a5 5 0 0 0-5 5v110a5 5 0 0 0 5 5h115c16.568 0 30-13.431 30-30v-5.96a28.88 28.88 0 0 0-17.5-26.54 25.75 25.75 0 0 0 12.5-22.077V30c0-16.568-13.432-30-30-30m-15 25h-60a5 5 0 0 0-5 5v12.5a5 5 0 0 0 5 5h60c5.522 0 10-4.477 10-10V35c0-5.523-4.478-10-10-10m-60 47.5h65c5.522 0 10 4.477 10 10V85c0 5.523-4.478 10-10 10h-65a5 5 0 0 1-5-5V77.5a5 5 0 0 1 5-5" fill="#1f1f1f" />
      <path d="M748.972 120h-27.928c-1.803 0-3.013-1.85-2.29-3.502L768.411 2.996A5 5 0 0 1 772.992 0h38.46a5 5 0 0 1 4.58 2.996l49.658 113.502c.722 1.652-.488 3.502-2.291 3.502h-27.928a5 5 0 0 1-4.569-2.969l-34.111-76.75c-1.76-3.96-7.379-3.96-9.138 0l-34.112 76.75a5 5 0 0 1-4.569 2.969m176.93 0h-25a5 5 0 0 1-5-5V5a5 5 0 0 1 5-5h35.145a30 30 0 0 1 26.007 15.046l44.186 76.837c1.27 2.22 4.66 1.314 4.66-1.246V5a5 5 0 0 1 5-5h25a5 5 0 0 1 5 5v110c0 2.761-2.24 5-5 5h-35.14a30 30 0 0 1-26.009-15.046L935.57 28.117c-1.277-2.22-4.668-1.314-4.668 1.246V115a5 5 0 0 1-5 5" fill="#1f1f1f" />
    </svg>
  );
}


export function LogoutIcon({ size = 16, style }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      style={{ display: 'inline-block', verticalAlign: 'middle', paddingLeft: 1, marginRight: 8, ...(style || {}) }}
    >
      <rect width="16" height="16" fill="none" />
      <path d="M14,14l0,-12l-6,0l0,-2l8,0l0,16l-8,0l0,-2l6,0Zm-9.002,-0.998l-4.998,-5.002l5,-5l1.416,1.416l-2.588,2.584l8.172,0l0,2l-8.172,0l2.586,2.586l-1.416,1.416Z" />
    </svg>
  );
} 

export function SupportIcon({ size = 20, color = 'currentColor', style }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 20 20"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      style={{ display: 'inline-block', marginRight: 8, verticalAlign: 'middle', ...(style || {}) }}
    >
      <path
        fillRule="evenodd"
        d="M0 10C0 4.478 4.478 0 10 0c5.523 0 10 4.478 10 10 0 5.523-4.477 10-10 10-5.522 0-10-4.477-10-10zm11.125 2.002H8.989v-.141c.01-1.966.492-2.254 1.374-2.782.093-.056.19-.114.293-.178.73-.459 1.292-1.038 1.292-1.883 0-.948-.743-1.564-1.666-1.564-.851 0-1.657.398-1.712 1.533H6.304C6.364 4.693 8.18 3.5 10.294 3.5c2.306 0 3.894 1.447 3.894 3.488 0 1.382-.695 2.288-1.805 2.952l-.238.144c-.79.475-1.009.607-1.02 1.777V12zm.17 3.012a1.344 1.344 0 01-1.327 1.328 1.32 1.32 0 01-1.328-1.328 1.318 1.318 0 011.328-1.316c.712 0 1.322.592 1.328 1.316z"
        fill={color}
      />
    </svg>
  );
}

function EmptyStateIllustration() {
  return (
    <svg width="120" height="120" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Background blobs/clouds */}
      <path d="M40 90C30 90 25 80 30 70C32 66 38 65 40 65C42 55 55 50 65 55C75 50 88 55 90 65C92 65 98 66 100 70C105 80 100 90 90 90H40Z" fill="#f1f5f9"/>
      <circle cx="20" cy="40" r="3" fill="#cbd5e1" />
      <circle cx="95" cy="30" r="4" fill="#cbd5e1" />
      <circle cx="25" cy="85" r="2.5" fill="#cbd5e1" />
      <circle cx="105" cy="75" r="2" fill="#cbd5e1" />
      
      {/* Clipboard base */}
      <rect x="40" y="30" width="44" height="56" rx="4" fill="#ffffff" stroke="#94a3b8" strokeWidth="2.5"/>
      <rect x="48" y="24" width="28" height="12" rx="3" fill="#e2e8f0" stroke="#94a3b8" strokeWidth="2.5"/>
      <rect x="56" y="20" width="12" height="4" rx="2" fill="#94a3b8"/>
      
      {/* Lines on clipboard */}
      <line x1="50" y1="50" x2="74" y2="50" stroke="#cbd5e1" strokeWidth="2.5" strokeLinecap="round"/>
      <line x1="50" y1="60" x2="74" y2="60" stroke="#cbd5e1" strokeWidth="2.5" strokeLinecap="round"/>
      <line x1="50" y1="70" x2="64" y2="70" stroke="#cbd5e1" strokeWidth="2.5" strokeLinecap="round"/>
    </svg>
  );
}

function KanbanSectionIcon({ tone }) {
  if (tone === 'todo') {
    return (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"/>
      </svg>
    );
  }
  if (tone === 'completed') {
    return (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 11.08V12a10 10 0 11-5.93-9.14" />
        <path d="M22 4L12 14.01l-3-3" />
      </svg>
    );
  }
  if (tone === 'overdue') {
    return (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <path d="M12 6v6l4 2" />
      </svg>
    );
  }
  return null;
}

export function SidebarIcon({ type }) {
  const common = {
    width: 18,
    height: 18,
    viewBox: '0 0 24 24',
    fill: 'none',
    xmlns: 'http://www.w3.org/2000/svg',
    'aria-hidden': 'true',
  };

  switch (type) {
    case 'dashboard':
      return (
        <svg {...common}>
          <rect x="4" y="4" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.8" />
          <rect x="13" y="4" width="7" height="4" rx="1.5" stroke="currentColor" strokeWidth="1.8" />
          <rect x="13" y="10" width="7" height="10" rx="1.5" stroke="currentColor" strokeWidth="1.8" />
          <rect x="4" y="13" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.8" />
        </svg>
      );
    case 'tasks':
      return (
        <svg {...common}>
          <rect x="4" y="5" width="16" height="14" rx="2.5" stroke="currentColor" strokeWidth="1.8" />
          <path d="M8 9h8M8 13h8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          <path d="M6.5 9l1 1 1.5-1.8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case 'team':
      return (
        <svg {...common}>
          <circle cx="9" cy="8" r="3" stroke="currentColor" strokeWidth="1.8" />
          <circle cx="17" cy="9" r="2.5" stroke="currentColor" strokeWidth="1.8" />
          <path d="M3.5 19a5.5 5.5 0 0 1 11 0" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          <path d="M13.5 19a4.5 4.5 0 0 1 7 0" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
      );
    case 'reporting':
      return (
        <svg {...common}>
          <path d="M4 19h16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          <path d="M6 16V10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          <path d="M11 16V6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          <path d="M16 16v-7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
      );
    case 'settings':
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.8" />
          <path d="M19.4 13a7.9 7.9 0 0 0 .1-2l2-1.2-2-3.4-2.2.7a8 8 0 0 0-1.7-1l-.3-2.3H10l-.3 2.3a8 8 0 0 0-1.7 1L5.8 6.4l-2 3.4L5.8 11a7.9 7.9 0 0 0 0 2l-2 1.2 2 3.4 2.2-.7a8 8 0 0 0 1.7 1l.3 2.3h5.2l.3-2.3a8 8 0 0 0 1.7-1l2.2.7 2-3.4z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
        </svg>
      );
    default:
      return null;
  }
}

// Add this function near the top with other helper functions (after getInitials)
export function getUserColor(userName) {
  if (!userName) return '#5b6ee1'; // fallback color
  
  // Create a hash of the user name
  let hash = 0;
  for (let i = 0; i < userName.length; i++) {
    hash = ((hash << 5) - hash) + userName.charCodeAt(i);
    hash = hash & hash; // Convert to 32bit integer
  }
  
  // Predefined palette of distinct colors
  const colors = [
    '#5b6ee1', // blue
    '#ec4899', // pink
    '#f97316', // orange
    '#06b6d4', // cyan
    '#8b5cf6', // purple
    '#10b981', // emerald
    '#f43f5e', // rose
    '#6366f1', // indigo
    '#14b8a6', // teal
    '#d97706', // amber
  ];
  
  return colors[Math.abs(hash) % colors.length];
}

function CustomDateRangePicker({ startDate, endDate, onDateChange }) {
  const [isOpen, setIsOpen] = useState(false);
  const [viewDate, setViewDate] = useState(new Date());
  const [hoverDate, setHoverDate] = useState(null);
  const [step, setStep] = useState(startDate && !endDate ? 'end' : 'start');
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const nextMonth = new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1);

  const handlePrevMonth = () => setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() - 1, 1));
  const handleNextMonth = () => setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1));

  const getDaysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();
  const getFirstDayOfMonth = (year, month) => new Date(year, month, 1).getDay();

  const parseDateLocal = (str) => {
    if (!str) return null;
    const [y, m, d] = str.split('-');
    return new Date(y, m - 1, d);
  };

  const formatDisplayDate = (dateStr) => {
    if (!dateStr) return 'MM/DD/YYYY';
    const [y, m, d] = dateStr.split('-');
    return `${m}/${d}/${y}`;
  };

  const handleDayClick = (date) => {
    const dateString = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    
    if (step === 'start') {
      onDateChange(dateString, '');
      setStep('end');
    } else {
      const start = parseDateLocal(startDate);
      if (date < start) {
        onDateChange(dateString, startDate); // Swap if end is before start
      } else {
        onDateChange(startDate, dateString);
      }
      setStep('start');
      setIsOpen(false);
    }
  };

  const renderCalendar = (year, month, position) => {
    const days = [];
    const daysInMonth = getDaysInMonth(year, month);
    const firstDay = getFirstDayOfMonth(year, month);
    
    for (let i = 0; i < firstDay; i++) days.push(null);
    for (let i = 1; i <= daysInMonth; i++) days.push(new Date(year, month, i));

    return (
      <div className="calendar-pane">
        <div className="calendar-header">
          {position === 'left' ? <button type="button" onClick={handlePrevMonth}>&lt;</button> : <div></div>}
          <span>{new Date(year, month).toLocaleString('default', { month: 'long', year: 'numeric' })}</span>
          {position === 'right' ? <button type="button" onClick={handleNextMonth}>&gt;</button> : <div></div>}
        </div>
        <div className="calendar-grid">
          {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, idx) => <div key={idx} className="calendar-day-name">{d}</div>)}
          {days.map((date, i) => {
            if (!date) return <div key={`empty-${i}`} className="calendar-cell empty"></div>;
            
            const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
            const isStart = startDate === dateStr;
            const isEnd = endDate === dateStr;
            
            let inRange = false;
            if (startDate && endDate) {
               inRange = date > parseDateLocal(startDate) && date < parseDateLocal(endDate);
            } else if (startDate && hoverDate && step === 'end') {
               const start = parseDateLocal(startDate);
               inRange = (date > start && date < hoverDate) || (date < start && date > hoverDate);
            }

            let classes = "calendar-cell";
            if (isStart) classes += " range-start";
            if (isEnd) classes += " range-end";
            if (inRange) classes += " in-range";

            return (
              <div 
                key={i} 
                className={classes}
                onClick={() => handleDayClick(date)}
                onMouseEnter={() => setHoverDate(date)}
              >
                {date.getDate()}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="date-range-wrapper" ref={wrapperRef}>
      <div className={`date-range-input ${isOpen ? 'active' : ''}`} onClick={() => setIsOpen(!isOpen)}>
        <span>{formatDisplayDate(startDate)} &ndash; {formatDisplayDate(endDate)}</span>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
          <line x1="16" y1="2" x2="16" y2="6"></line>
          <line x1="8" y1="2" x2="8" y2="6"></line>
          <line x1="3" y1="10" x2="21" y2="10"></line>
        </svg>
      </div>
      
      {isOpen && (
        <div className="date-range-popup">
          {renderCalendar(viewDate.getFullYear(), viewDate.getMonth(), 'left')}
          <div className="calendar-divider"></div>
          {renderCalendar(nextMonth.getFullYear(), nextMonth.getMonth(), 'right')}
        </div>
      )}
    </div>
  );
}


function TaskList({ user, onAuthError, onLogout, loggingOut = false, onNavigateToDashboard, onNavigateToTasks, onNavigateToFMS, onNavigateToRegister,onNavigateToReturns,onNavigateToDelegation,onNavigateToAllTasks,onNavigateToResourceControl,onNavigateToLogging }) {
  const [columns, setColumns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedTask, setSelectedTask] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showCreateTask, setShowCreateTask] = useState(false);
  const [viewMode, setViewMode] = useState('list');
  // const [toast, setToast] = useState(null);
  // Action Menu State
  const [actionMenuOpen, setActionMenuOpen] = useState(false);
  //selection
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedTaskIds, setSelectedTaskIds] = useState([]);
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);

  //bulk edit 
  const [showBulkEditModal, setShowBulkEditModal] = useState(false);
  const [bulkEditForm, setBulkEditForm] = useState({
    taskStatus: '',
    assignedTo: ''
  });

   useEffect(() => {
        document.title = 'Checklist Tasks ';
      }, []);

  // Edit Task State
 const [showEditTask, setShowEditTask] = useState(false);
  const [editForm, setEditForm] = useState({
    taskId: '',
    taskTitle: '',
    taskDescription: '',
    plannedDate: '',
    taskStatus: '',
    assignedTo: '',
    active: 1, // Add this line
    dateType: 'regular',
    frequency: '',
    skipDays: 1,
    recurrenceEndDate: '',
    weekdayFrequency: 'one_time',
    selectedWeekdays: [],
  });
  console.log(error);

  // Filters
  const [dateFilter, setDateFilter] = useState('today'); 
  const [customStart, setCustomStart] = useState(getTodayString());
  const [customEnd, setCustomEnd] = useState(getTodayString());

  // After the dateFilter, customStart, customEnd states, add:
const [departmentFilter, setDepartmentFilter] = useState('');
const [employeeFilter, setEmployeeFilter] = useState('');

 const [taskForm, setTaskForm] = useState({
  taskTitle: '',
  taskDescription: '',
  assignedTo: '',
  department: '',
  plannedDate: '',
  dateType: 'regular',  // Add this line
  frequency: '',
  skipDays: 1,
  recurrenceEndDate: '',
  weekdayFrequency: 'one_time',
  selectedWeekdays: [],
});;

  const [departments, setDepartments] = useState([]);
  const [departmentsLoading, setDepartmentsLoading] = useState(false);
  const [departmentsError, setDepartmentsError] = useState('');

  const [employees, setEmployees] = useState([]);
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const [employeesError, setEmployeesError] = useState('');
  
  const [confirmData, setConfirmData] = useState(null);
//   const showToast = (message, duration = 3000) => {
//   setToast({ message, id: Date.now() });
//   setTimeout(() => setToast(null), duration);
// };
  const [deleteConfirmData, setDeleteConfirmData] = useState(null);
  const [statusSaving, setStatusSaving] = useState(false);
  const [creatingTask, setCreatingTask] = useState(false);

  const [draggingTaskId, setDraggingTaskId] = useState(null);
  const [dropZone, setDropZone] = useState('');

  const isDraggableSource = (columnKey) =>
    columnKey === 'todo' || columnKey === 'completed' || columnKey === 'overdue';

  const handleDragStart = (task, columnKey) => {
    if (loading || statusSaving) return;
    if (!isDraggableSource(columnKey)) return;

    setDraggingTaskId(task.id);
  };

  const handleDragEnd = () => {
    setDraggingTaskId(null);
    setDropZone('');
  };

  const handleDragOver = (e, columnKey) => {
    if (loading || statusSaving) return;

    if (columnKey !== 'todo' && columnKey !== 'completed') return;

    e.preventDefault();
    setDropZone(columnKey);
  };

  const handleDrop = async (columnKey) => {
    if (loading || statusSaving) return;

    const draggedTask = allTasks.find((t) => t.id === draggingTaskId);
    if (!draggedTask) return;

    if (columnKey !== 'todo' && columnKey !== 'completed') return;

    const nextStatus = columnKey === 'completed' ? 'Completed' : 'Pending';

    // --- ADD VALIDATION BLOCK HERE IN THE FUTURE IF NEEDED ---

    try {
      await updateTaskStatus(draggedTask, nextStatus);
    } finally {
      setDraggingTaskId(null);
      setDropZone('');
    }
  };

  const handleToggleTaskSelection = (taskId, e) => {
    if (e) e.stopPropagation();
    setSelectedTaskIds(prev =>
      prev.includes(taskId) ? prev.filter(id => id !== taskId) : [...prev, taskId]
    );
  };

 const handleBulkDelete = async () => {
    setStatusSaving(true);
    try {
      const res = await fetch('/api/tasks/delete_task', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ task_ids: selectedTaskIds }),
      });

      const data = await res.json().catch(() => ({}));

      if (res.status === 401) {
        onAuthError();
        return;
      }
      
      if (!res.ok) {
        throw new Error(data.message || 'Unable to mark tasks inactive');
      }

      // Success: Reset UI and reload tasks
      setSelectionMode(false);
      setSelectedTaskIds([]);
      setSelectedTask(null);
      await loadTasks();
      toast.success(`✓ ${selectedTaskIds.length} tasks marked inactive successfully`);
      
    } catch (err) {
      // Show error explicitly so you know if it fails
      alert(err.message || 'Failed to mark tasks inactive. Please check your backend.');
    } finally {
      // ALWAYS close the modal and turn off loading state
      setStatusSaving(false);
      setShowBulkDeleteConfirm(false); 
    }
  };


  const handleBulkEdit = async () => {
    setStatusSaving(true);
    try {
      // Create an array of update requests for all selected tasks
      const promises = selectedTaskIds.map(async (id) => {
        const task = allTasks.find(t => t.taskId === id);
        if (!task) return;

        const res = await fetch('/api/tasks/update_task_details', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            task_id: task.taskId,
            task_title: task.title,
            task_description: task.description === `Task #${task.taskId}` ? '' : task.description,
            planned_date: task.plannedDate,
            // If the bulk form is empty, keep the original task value
            task_status: bulkEditForm.taskStatus || task.status,
            assigned_to: bulkEditForm.assignedTo ? Number(bulkEditForm.assignedTo) : task.assignedTo
          }),
        });

        if (res.status === 401) onAuthError();
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.message || `Failed to update task ${task.taskId}`);
        }
      });

      // Wait for all updates to finish
      await Promise.all(promises);

      // Reset and reload
      setShowBulkEditModal(false);
      setSelectionMode(false);
      setSelectedTaskIds([]);
      setBulkEditForm({ taskStatus: '', assignedTo: '' });
      await loadTasks();
      toast.success(` ${selectedTaskIds.length} tasks updated successfully`);
      
    } catch (err) {
      alert(err.message || 'Failed to bulk edit tasks.');
    } finally {
      setStatusSaving(false);
    }
  };


 const loadTasks = useCallback(async () => {
  if (dateFilter === 'custom' && (!customStart || !customEnd)) {
    return;
  }

  setLoading(true);
  setError('');

  try {
    let url = `/api/tasks/task_lists?date_filter=${dateFilter}`;
    if (dateFilter === 'custom') {
      url += `&start_date=${customStart}&end_date=${customEnd}`;
    }

    if (employeeFilter) {
      url += `&employee_filter=${employeeFilter}`;
    }

     if (departmentFilter) {
      url += `&department_filter=${departmentFilter}`;
    }

    

    

      const res = await fetch(url, { credentials: 'include' });
      const data = await res.json().catch(() => ({}));

      if (res.status === 401) {
        onAuthError();
        return;
      }

      if (!res.ok) {
        throw new Error(data.message || 'Unable to load tasks');
      }

      const incomingColumns = Array.isArray(data.columns) ? data.columns : [];
      
      setColumns(incomingColumns);
      
      // Keep selected task active if it still exists in the new data
      if (selectedTask) {
         const stillExists = incomingColumns.some(col => col.cards.some(t => t.id === selectedTask.id));
         if (!stillExists) setSelectedTask(incomingColumns?.[0]?.cards?.[0] || null);
      } else {
         setSelectedTask(incomingColumns?.[0]?.cards?.[0] || null);
      }

    } catch (err) {
      setError(err.message || 'Unable to load tasks');
      setColumns([]);
      setSelectedTask(null);
    } finally {
      setLoading(false);
    }
  }, [onAuthError, dateFilter, customStart, customEnd,selectedTask,departmentFilter,employeeFilter]);

  const loadDepartments = useCallback(async () => {
    setDepartmentsLoading(true);
    setDepartmentsError('');

    try {
      const res = await fetch('/api/tasks/departments', { credentials: 'include' });
      const data = await res.json().catch(() => ({}));

      if (res.status === 401) {
        onAuthError();
        return;
      }

      if (!res.ok) {
        throw new Error(data.message || 'Unable to load departments');
      }

      setDepartments(Array.isArray(data) ? data : []);
    } catch (err) {
      setDepartments([]);
      setDepartmentsError(err.message || 'Unable to load departments');
    } finally {
      setDepartmentsLoading(false);
    }
  }, [onAuthError]);

  const loadEmployees = useCallback(async () => {
    setEmployeesLoading(true);
    setEmployeesError('');

    try {
      const res = await fetch('/api/tasks/users', { credentials: 'include' });
      const data = await res.json().catch(() => ({}));

      if (res.status === 401) {
        onAuthError();
        return;
      }

      if (!res.ok) {
        throw new Error(data.message || 'Unable to load employees');
      }

      setEmployees(Array.isArray(data) ? data : []);
    } catch (err) {
      setEmployees([]);
      setEmployeesError(err.message || 'Unable to load employees');
    } finally {
      setEmployeesLoading(false);
    }
  }, [onAuthError]);

 const updateTaskStatus = async (task, status) => {
    setStatusSaving(true);
    setError('');

    try {
      const res = await fetch('/api/tasks/update_task_status', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ task_id: task.taskId, task_status: status }),
      });

      const data = await res.json().catch(() => ({}));

      if (res.status === 401) {
        onAuthError();
        return;
      }
      if (!res.ok) {
        throw new Error(data.message || 'Unable to update task status');
      }

      // 👇 ADD THIS BLOCK HERE (before loadTasks):
      if (data.statusChangeType === 'marked_completed') {
        toast.success('Task completed successfully', 3000);
      } else if (data.statusChangeType === 'moved_from_completed') {
        toast.success('Task moved out from complete state', 3000);
      } else {
        toast.success(`Task status updated to ${data.status}`, 3000);
      }

      await loadTasks();
    } catch (err) {
      toast.error(err.message || 'Unable to update task status');
      setError(err.message || 'Unable to update task status');
    } finally {
      setStatusSaving(false);
    }
  };

  const requestStatusChange = (task, nextStatus, message) => {
    if (statusSaving || loading || loggingOut) return;

    // --- ADD FRONTEND VALIDATION HERE ---

    setConfirmData({ task, nextStatus, message });
  };

  const handleChecklistToggle = (item) => {
    if (item.text !== 'Marked completed' || !activeTask) return;

    const currentlyCompleted = item.done;
    const nextStatus = currentlyCompleted ? 'Pending' : 'Completed';
    const message = currentlyCompleted
      ? 'This task is completed. Do you want to change it back to pending?'
      : 'Mark this task as completed?';

    requestStatusChange(activeTask, nextStatus, message);
  };

  const handleRowStatusToggle = (task) => {
  const currentlyCompleted = task.status === 'Completed';
  const nextStatus = currentlyCompleted ? 'Pending' : 'Completed';
  const message = currentlyCompleted
    ? 'This task is completed. Do you want to change it back to pending?'
    : 'Mark this task as completed?';

  requestStatusChange(task, nextStatus, message);
};

  useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  useEffect(() => {
    loadDepartments();
  }, [loadDepartments]);

  useEffect(() => {
    loadEmployees();
  }, [loadEmployees]);

  const allTasks = useMemo(
    () =>
      columns.flatMap((column) =>
        (column.cards || []).map((card) => ({ ...card, columnTitle: column.title }))
      ),
    [columns]
  );

  const boardColumns = useMemo(() => {
    const todo = [];
    const completed = [];
    const overdue = [];

    allTasks.forEach((task) => {
      if (String(task.status || '').toLowerCase() === 'completed') {
        completed.push(task);
      } else if (isOverdueTask(task)) {
        overdue.push(task);
      } else {
        todo.push(task);
      }
    });

    const sortByDate = (a, b) => {
      const aDate = parseDateOnly(a.plannedDate || a.due)?.getTime() ?? Number.MAX_SAFE_INTEGER;
      const bDate = parseDateOnly(b.plannedDate || b.due)?.getTime() ?? Number.MAX_SAFE_INTEGER;
      return aDate - bDate;
    };

    todo.sort(sortByDate);
    completed.sort(sortByDate);
    overdue.sort(sortByDate);

    return [
      { key: 'todo', title: 'To Do', count: todo.length, tasks: todo, tone: 'todo' },
      { key: 'completed', title: 'Completed', count: completed.length, tasks: completed, tone: 'completed' },
      { key: 'overdue', title: 'Overdue', count: overdue.length, tasks: overdue, tone: 'overdue' },
    ];
  }, [allTasks]);

  const activeTask = selectedTask || allTasks[0] || null;
  const activeChecklist = activeTask?.checklist || [];
  const checklistDone = activeChecklist.filter((item) => item.done).length;

const handleTaskClick = (task) => {
    if (selectionMode) {
      handleToggleTaskSelection(task.taskId);
      return;
    }
    setSelectedTask(task);
    setDetailsOpen(true);
  };
  const handleTaskFormChange = (e) => {
    const { name, value } = e.target;
    setTaskForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setEditForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleToggleRecurring = async (task) => {
    if (!task || statusSaving) return;
    setStatusSaving(true);
    setError('');

    const nextRecurringActive = Number(task.recurringActive) === 1 ? 0 : 1;
    try {
      const res = await fetch('/api/tasks/toggle_recurring', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          task_id: task.taskId,
          recurring_active: nextRecurringActive,
        }),
      });

      const data = await res.json().catch(() => ({}));
      if (res.status === 401) {
        onAuthError();
        return;
      }
      if (!res.ok) throw new Error(data.message || 'Unable to update recurring task');

      setSelectedTask((prev) => prev ? { ...prev, recurringActive: nextRecurringActive } : prev);
      await loadTasks();
      toast.success(nextRecurringActive ? 'Recurring task resumed' : 'Recurring task stopped');
    } catch (err) {
      toast.error(err.message || 'Unable to update recurring task');
    } finally {
      setStatusSaving(false);
    }
  };

  const handleCreateTask = async (e) => {
    e.preventDefault();
    setError('');
    const basePayload = {
      task_title: taskForm.taskTitle.trim(),
      task_description: taskForm.taskDescription.trim(),
      assigned_to: Number(taskForm.assignedTo),
      department_id: Number(taskForm.department),
    };

    if (
      !basePayload.task_title ||
      !basePayload.task_description ||
      !basePayload.assigned_to ||
      !basePayload.department_id ||
      !taskForm.plannedDate
    ) {
      setError('Please fill all create task fields.');
      return;
    }

    if (taskForm.dateType === 'dailyRange' && taskForm.recurrenceEndDate < taskForm.plannedDate) {
      setError('End date must be on or after the start date.');
      return;
    }

    const payloads = [];
    if (taskForm.dateType === 'weekdays') {
      const selectedDates = getWeekdayPatternDates(taskForm.plannedDate, taskForm.selectedWeekdays);
      if (!taskForm.weekdayFrequency || selectedDates.length === 0) {
        setError('Please select a pattern frequency and at least one weekday.');
        return;
      }

      selectedDates.forEach((date) => {
        const plannedDate = formatLocalDate(date);
        payloads.push({
          ...basePayload,
          planned_date: plannedDate,
          task_frequency: buildWeekdayPatternFrequency(taskForm.weekdayFrequency, plannedDate),
        });
      });
    } else {
      const taskFrequency = buildChecklistFrequency(taskForm);
      if (!taskFrequency) {
        setError('Please select a frequency or recurrence pattern.');
        return;
      }
      payloads.push({
        ...basePayload,
        planned_date: taskForm.plannedDate,
        task_frequency: taskFrequency,
      });
    }

    setCreatingTask(true);

    try {
      for (const payload of payloads) {
        const res = await fetch('/api/tasks/create_task', {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        });

        const data = await res.json().catch(() => ({}));

        if (res.status === 401) {
          onAuthError();
          return;
        }

        if (!res.ok) {
          throw new Error(data.message || 'Unable to create task');
        }
      }

      setShowCreateTask(false);
      setTaskForm({
        taskTitle: '',
        taskDescription: '',
        assignedTo: '',
        department: '',
        plannedDate: '',
        dateType: 'regular',
        frequency: '',
        skipDays: 1,
        recurrenceEndDate: '',
        weekdayFrequency: 'one_time',
        selectedWeekdays: [],
      });

      await loadTasks();
    } catch (err) {
      setError(err.message || 'Unable to create task');
    } finally {
      setCreatingTask(false);
    }
  };

  const handleEditTask = async (e) => {
    e.preventDefault();
    
    if (!editForm.taskTitle.trim() || !editForm.taskDescription.trim() || !editForm.plannedDate) {
      return;
    }

    const taskFrequency = buildChecklistFrequency(editForm);
    if (!taskFrequency) {
      toast.error('Please select a frequency or recurrence pattern.');
      return;
    }

    if (editForm.dateType === 'dailyRange' && editForm.recurrenceEndDate < editForm.plannedDate) {
      toast.error('End date must be on or after the start date.');
      return;
    }

    setCreatingTask(true);

    try {
     const res = await fetch('/api/tasks/update_task_details', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
       body: JSON.stringify({
          task_id: editForm.taskId,
          task_title: editForm.taskTitle.trim(),
          task_description: editForm.taskDescription.trim(),
          planned_date: editForm.plannedDate,
          task_frequency: taskFrequency,
          task_status: editForm.taskStatus,
          assigned_to: editForm.assignedTo,
          active: editForm.active // Add this line
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (res.status === 401) {
        onAuthError();
        return;
      }

      if (!res.ok) {
        throw new Error(data.message || 'Unable to update task');
      }

      setShowEditTask(false);
      
      // Update local activeTask state dynamically without losing focus
      setSelectedTask(prev => prev ? {
        ...prev, 
        title: editForm.taskTitle.trim(),
        description: editForm.taskDescription.trim(),
        plannedDate: editForm.plannedDate,
        due: editForm.plannedDate,
        status: editForm.taskStatus, // Add this line
        frequency: taskFrequency,
      } : null);

      await loadTasks();
    } catch (err) {
      toast.error(err.message || 'Unable to update task');
    } finally {
      setCreatingTask(false);
    }
  };

  const handleDeleteTask = async () => {
    if (!deleteConfirmData) return;
    setStatusSaving(true);
    
    try {
      const res = await fetch('/api/tasks/delete_task', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ task_id: deleteConfirmData.taskId }),
      });

      if (res.status === 401) {
        onAuthError();
        return;
      }

      if (!res.ok) throw new Error('Unable to mark task inactive');

      setDeleteConfirmData(null);
      setSelectedTask(null);
      await loadTasks();
    } catch (err) {
      toast.error(err.message || 'Failed to mark task inactive');
    } finally {
      setStatusSaving(false);
    }
  };

 const openEditModal = () => {
    if (!activeTask) return;
    const recurrence = parseChecklistFrequency(activeTask.frequency);
    setEditForm({
      taskId: activeTask.taskId,
      taskTitle: activeTask.title,
      taskDescription: activeTask.description === `Task #${activeTask.taskId}` ? '' : activeTask.description,
      plannedDate: activeTask.plannedDate || '',
      taskStatus: activeTask.status || 'To Do',
      assignedTo: activeTask.assignedTo || '',
      active: activeTask.active !== undefined ? activeTask.active : 1, // Add this line
      dateType: recurrence.dateType,
      frequency: recurrence.frequency,
      skipDays: recurrence.skipDays,
      recurrenceEndDate: recurrence.recurrenceEndDate,
      weekdayFrequency: recurrence.weekdayFrequency,
      selectedWeekdays: recurrence.selectedWeekdays,
    });
    setShowEditTask(true);
  };

  return (
    <div className="app-shell">
      <AppSidebar
        user={user}
        active="tasks"
        onLogout={onLogout}
        loggingOut={loggingOut}
        onCreateTask={() => setShowCreateTask(true)}
        onNavigateToDashboard={onNavigateToDashboard}
        onNavigateToTasks={onNavigateToTasks}
        onNavigateToFMS={onNavigateToFMS}
        onNavigateToReturns={onNavigateToReturns}
        onNavigateToDelegation={onNavigateToDelegation}
        onNavigateToAllTasks={onNavigateToAllTasks}
        onNavigateToResourceControl={onNavigateToResourceControl}
        onNavigateToLogging={onNavigateToLogging}
      />


      <main className="workspace">
        <header className="fms-topbar rrl-topbar">
                          <div className="mobile-left">
                            <button className="mobile-menu-btn" type="button" aria-label="Open menu" />
                          </div>
                
                          <div className="fms-global-search">
                            <input 
                              type="search" 
                              placeholder="Search orders, customers, or products..." 
                              // value={searchTerm}
                              // onChange={(e) => setSearchTerm(e.target.value)}
                            />
                          </div>
                
                           <div className="topbar-actions">
                            {/* <button className="icon-btn icon-view desktop-view-trigger" type="button" aria-label="View options" />
                            <button className="icon-btn icon-view mobile-nav-trigger" type="button" aria-label="Open navigation" /> */}
                            <div className="avatar-placeholder" style={{ background: getUserColor(user?.emp_name || user?.username) }}>
                              {getInitials(user)}
                            </div>
                          </div>
                        </header>

        <section className={`board-shell ${detailsOpen && activeTask ? 'with-details' : ''}`}>
          <div className={`board-main ${loading ? 'is-loading' : ''}`} aria-busy={loading}>
            <div className="board-header-row">
              <div>
                <h2>Active Tasks</h2>
                <p>{allTasks.length} tasks matching criteria</p>
              </div>

              <div className="board-tools">
                <select
                  className="tool-btn"
                  value={dateFilter}
                  onChange={(e) => setDateFilter(e.target.value)}
                  style={{ cursor: 'pointer', outline: 'none' }}
                >
                  <option value="today">Today</option>
                  {/* <option value="yesterday">Yesterday</option> */}
                  {/* <option value="tomorrow">Tomorrow</option> */}
                  {/* <option value="overdue">Overdue Tasks</option> */}
                  <option value="all">All Tasks</option>
                  <option value="custom">Custom Range</option>
                </select>


                {(user?.role === 1 || user?.role === 2) && (
                  <select
                    className="tool-btn"
                    value={employeeFilter}
                    onChange={(e) => setEmployeeFilter(e.target.value)}
                    style={{ cursor: 'pointer', outline: 'none' }}
                  >
                    <option value="">All Employees</option>
                    {employees.map((emp) => (
                      <option key={emp.id} value={emp.id}>
                        {emp.emp_name}
                      </option>
                    ))}
                  </select>
                )}


                {(user?.role === 1 || user?.role === 2) && (
                  <select
                    className="tool-btn"
                    value={departmentFilter}
                    onChange={(e) => setDepartmentFilter(e.target.value)}
                    style={{ cursor: 'pointer', outline: 'none' }}
                  >
                    <option value="">All Departments</option>
                    {departments.map((dept) => (
                      <option key={dept.id} value={dept.id}>
                        {dept.dept_name}
                      </option>
                    ))}
                  </select>
                )}

             {dateFilter === 'custom' && (
                  <CustomDateRangePicker 
                    startDate={customStart}
                    endDate={customEnd}
                    onDateChange={(start, end) => {
                      setCustomStart(start);
                      setCustomEnd(end);
                    }}
                  />
                )}

                {(user?.role === 1 || user?.role === 2) && (
                  <>
                    <button
                      className="tool-btn"
                      onClick={() => {
                        setSelectionMode(!selectionMode);
                        setSelectedTaskIds([]);
                      }}
                      style={{
                        background: selectionMode ? '#eef2ff' : '#fff',
                        borderColor: selectionMode ? '#4f46e5' : 'var(--border)',
                        color: selectionMode ? '#4f46e5' : '#41516e',
                        fontWeight: selectionMode ? 700 : 400
                      }}
                    >
                      {selectionMode ? 'Cancel Selection' : 'Select Tasks'}
                    </button>

                  {selectionMode && selectedTaskIds.length > 0 && (
                      <>
                        <button
                          type="button"
                          className="tool-btn"
                          onClick={() => setShowBulkEditModal(true)}
                          style={{ background: '#f0fdf4', color: '#16a34a', borderColor: '#bbf7d0', fontWeight: 700 }}
                        >
                          Edit ({selectedTaskIds.length})
                        </button>
                        
                        <button
                          type="button" 
                          className="tool-btn"
                          onClick={() => setShowBulkDeleteConfirm(true)}
                          style={{ background: '#fef2f2', color: '#ef4444', borderColor: '#fca5a5', fontWeight: 700 }}
                        >
                          Mark Inactive ({selectedTaskIds.length})
                        </button>
                      </>
                    )}
                  </>
                )}

                <div className="view-toggle">
                  <button
                    className={`view-btn ${viewMode === 'list' ? 'active' : ''}`}
                    type="button"
                    onClick={() => setViewMode('list')}
                  >
                    List
                  </button>
                  <button
                    className={`view-btn ${viewMode === 'board' ? 'active' : ''}`}
                    type="button"
                    onClick={() => setViewMode('board')}
                  >
                    Board
                  </button>
                </div>
              </div>
            </div>

            {allTasks.length ? (
              viewMode === 'list' ? (
                <div 
                  className="task-list" 
                  style={{ 
                    padding: 0, 
                    gap: 0, 
                    backgroundColor: '#fff', 
                    borderRadius: '8px', 
                    border: '1px solid #e5e9f2', 
                    overflowY: 'auto' 
                  }}
                >
                  {/* Sticky Table Header Row */}
                  <div style={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 10,
                    display: 'grid',
                    gridTemplateColumns: 'minmax(300px, 3fr) 100px 120px 180px 100px 40px',
                    gap: '16px',
                    padding: '14px 16px',
                    fontSize: '12px',
                    fontWeight: '700',
                    color: '#475569',
                    backgroundColor: '#f8fafc',
                    borderBottom: '1px solid #e2e8f0',
                    alignItems: 'center'
                  }}>
                    <div style={{ paddingLeft: '32px' }}>Task</div>
                    <div>Details</div>
                    <div>Status</div>
                    <div>Assignee</div>
                    <div>Due Date</div>
                    <div></div>
                  </div>

                  {/* Task Rows */}
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    {allTasks.map((task) => {
                      const isBulkSelected = selectedTaskIds.includes(task.taskId);
                      const isActiveRow = !selectionMode && activeTask?.id === task.id;

                      return (
                      <div
                        key={task.id}
                        className={`task-row ${isActiveRow ? 'selected' : ''} ${isBulkSelected ? 'bulk-selected' : ''}`}
                        onClick={() => handleTaskClick(task)}
                        style={{
                          display: 'grid',
                          gridTemplateColumns: 'minmax(300px, 3fr) 100px 120px 180px 100px 40px',
                          gap: '16px',
                          alignItems: 'center',
                          minHeight: '60px',
                          padding: '10px 16px',
                          cursor: 'pointer',
                          backgroundColor: isBulkSelected ? '#eef2ff' : isActiveRow ? '#d3dfff' : '#fff',
                          borderBottom: '1px solid #f1f5f9',
                          transition: 'background-color 0.15s ease'
                        }}
                        onMouseEnter={(e) => {
                          if (!isBulkSelected && !isActiveRow) e.currentTarget.style.backgroundColor = '#f8fafc';
                        }}
                        onMouseLeave={(e) => {
                          if (isBulkSelected) {
                            e.currentTarget.style.backgroundColor = '#eef2ff';
                          } else if (isActiveRow) {
                            e.currentTarget.style.backgroundColor = '#d3dfff';
                          } else {
                            e.currentTarget.style.backgroundColor = '#fff';
                          }
                        }}
                      >
                        {/* 1. Task (Checkbox + Icon + Title) */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '14px', minWidth: 0 }}>
                          <span
                            className={`task-check ${task.status === 'Completed' ? 'checked' : ''} ${
                              loading || statusSaving ? 'disabled' : ''
                            }`}
                            role="button"
                            tabIndex={loading || statusSaving ? -1 : 0}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (loading || statusSaving) return;
                              if (selectionMode) {
                                handleToggleTaskSelection(task.taskId, e);
                                return;
                              }
                              handleRowStatusToggle(task);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                e.stopPropagation();
                                if (loading || statusSaving) return;
                                if (selectionMode) {
                                  handleToggleTaskSelection(task.taskId, e);
                                  return;
                                }
                                handleRowStatusToggle(task);
                              }
                            }}
                            aria-label={
                              selectionMode
                                ? isBulkSelected ? 'Remove task from selection' : 'Select task'
                                : task.status === 'Completed' ? 'Reopen task' : 'Mark task complete'
                            }
                            aria-disabled={loading || statusSaving}
                            style={{ flexShrink: 0, margin: 0, width: '16px', height: '16px', borderRadius: '4px', border: '1px solid #cbd5e1' }}
                          />
                          <div style={{ width: 28, height: 28, borderRadius: '6px', background: '#eef2ff', display: 'grid', placeItems: 'center', flexShrink: 0 }}>
                            <span style={{ fontSize: '14px' }}>📋</span>
                          </div>
                          <h3 style={{ margin: 0, fontSize: '14px', fontWeight: 600, color: '#1e293b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {task.title}
                          </h3>
                        </div>

                        {/* 2. Details (UID) */}
                        <div style={{ fontSize: '13px', color: '#64748b' }}>
                          UID: {task.uid || '-'}
                        </div>

                        {/* 3. Status */}
                        <div>
                          <span style={{ 
                            display: 'inline-flex', 
                            alignItems: 'center', 
                            padding: '4px 10px', 
                            borderRadius: '999px', 
                            fontSize: '12px', 
                            fontWeight: 700,
                            backgroundColor: isOverdueTask(task) ? '#fee2e2' : task.status === 'Completed' ? '#dcfce7' : '#dee6ff',
                            color: isOverdueTask(task) ? '#ef4444' : task.status === 'Completed' ? '#16a34a' : '#7975e7'
                          }}>
                            {isOverdueTask(task) ? 'Overdue' : (task.status || 'Pending')}
                          </span>
                        </div>

                        {/* 4. Assignee (Initials Circle + Name) */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
                          <div 
                            className="avatar-placeholder" 
                            title={`Assigned to: ${task.assigneeName}`} 
                            style={{ width: 28, height: 28, fontSize: '11px', flexShrink: 0, borderRadius: '50%', background: getUserColor(task.assigneeName), color: '#fff', display: 'grid', placeItems: 'center', fontWeight: 'bold' }}
                          >
                            {getInitials({ emp_name: task.assigneeName })}
                          </div>
                          <span style={{ fontSize: '13px', color: '#475569', fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {task.assigneeName || 'Unassigned'}
                          </span>
                        </div>

                        {/* 5. Due Date / Planned Date */}
                        <div style={{ fontSize: '13px', color: '#475569', fontWeight: 500 }}>
                          {task.plannedDate ? formatDateStr(task.plannedDate) : '-'}
                        </div>

                        {/* 6. Actions (...) */}
                        {/* <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                           <button className="icon-btn small icon-menu" type="button" aria-label="More actions" style={{ background: 'transparent', color: '#94a3b8' }} />
                        </div> */}
                      </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div className="kanban-board">
                  {boardColumns.map((column) => (
                    <div
                      key={column.key}
                      className={`k-col k-col-${column.tone} ${dropZone === column.tone ? 'drop-zone' : ''}`}
                      onDragOver={(e) => handleDragOver(e, column.tone)}
                      onDrop={() => handleDrop(column.tone)}
                    >
                      <div className={`k-col-header k-head-${column.tone}`}>
                        <div className="k-col-title">
                          <KanbanSectionIcon tone={column.tone} />
                          <h3>{column.title.toUpperCase()} ({column.count})</h3>
                        </div>
                      </div>

                      <div className="kanban-cards">
                        {column.tasks.length ? (
                          column.tasks.map((task) => (
                           <button
                              key={task.id}
                              type="button"
                              draggable={!selectionMode && (column.tone === 'todo' || column.tone === 'completed' || column.tone === 'overdue')}
                              onDragStart={() => handleDragStart(task, column.tone)}
                              onDragEnd={handleDragEnd}
                              className={`k-card ${!selectionMode && activeTask?.id === task.id ? 'selected' : ''} ${draggingTaskId === task.id ? 'dragging' : ''} ${selectedTaskIds.includes(task.taskId) ? 'bulk-selected' : ''}`}
                              onClick={() => handleTaskClick(task)}
                              aria-pressed={selectionMode ? selectedTaskIds.includes(task.taskId) : undefined}
                            >
                              <div className="k-card-inner">
                                {/* Left Checkbox */}
                                <input
                                  type="checkbox"
                                  className="k-card-check"
                                  checked={selectionMode ? selectedTaskIds.includes(task.taskId) : task.status === 'Completed'}
                                  aria-label={selectionMode ? (selectedTaskIds.includes(task.taskId) ? 'Task selected' : 'Task not selected') : (task.status === 'Completed' ? 'Task completed' : 'Mark task complete')}
                                  onChange={(e) => {
                                    e.stopPropagation();
                                    if (selectionMode) {
                                      handleToggleTaskSelection(task.taskId, e);
                                    } else {
                                      handleRowStatusToggle(task);
                                    }
                                  }}
                                />
                                
                                {/* Clipboard Icon */}
                                <div className="k-card-icon">📋</div>
                                
                                {/* Main Content */}
                                <div className="k-card-main">
                                  <h4 className="k-card-title">{task.title}</h4>

                                  <div style={{ fontSize: '11px', color: '#64748b', marginBottom: '6px' }}>
                                    UID: {task.uid || '-'}
                                  </div>
                                  
                                  <div className="k-card-meta">
                                    <div className="k-card-info">
                                      <span className={`k-status-text text-${column.tone}`}>{column.title}</span>
                                      <span className="k-date-text">
                                        {column.tone === 'completed' && task.deliveredDate ? formatDateStr(task.deliveredDate) : 
                                         column.tone === 'overdue' && task.due ? formatDateStr(task.due) : 
                                         task.plannedDate ? formatDateStr(task.plannedDate) : '-'}
                                      </span>
                                    </div>
                                    <div
                                      className="avatar-placeholder"
                                      title={`Assigned to: ${task.assigneeName}`}
                                      style={{ width: 26, height: 26, fontSize: '10px', flexShrink: 0, background: getUserColor(task.assigneeName) }}
                                    >
                                      {getInitials({ emp_name: task.assigneeName })}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </button>
                          ))
                        ) : (
                          <div className="k-empty-state">
                            <EmptyStateIllustration />
                            <p>No tasks here</p>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )
            ) : loading ? (
              <div className="task-empty">Loading tasks...</div>
            ) : (
              <div className="task-empty">No tasks are assigned to you for this date range.</div>
            )}
          </div>

          {detailsOpen && activeTask ? (
            <aside className="details-panel">
              <div className="details-top">
                <span className={`priority-tag ${getStatusClass(activeTask.status)}`}>
                  {activeTask.status}
                </span>

                <div className="details-actions">
                  {(user?.role === 1 || user?.role === 2) && (
                    <>
                      <button 
                        className="icon-btn small icon-edit" 
                        type="button" 
                        aria-label="Edit task" 
                        onClick={openEditModal} 
                      />
                      <div style={{ position: 'relative' }}>
                        <button 
                          className="icon-btn small icon-menu" 
                          type="button" 
                          aria-label="More actions" 
                          onClick={() => setActionMenuOpen(!actionMenuOpen)} 
                        />
                        {actionMenuOpen && (
                          <div 
                            className="fd-menu" 
                            style={{ position: 'absolute', top: 'calc(100% + 4px)', right: 0, left: 'auto', minWidth: '120px', zIndex: 100, padding: '4px' }}
                          >
                            <button
                              type="button"
                              className="fd-option"
                              style={{ color: '#dc2626', fontWeight: 600 }}
                              onClick={() => {
                                setActionMenuOpen(false);
                                setDeleteConfirmData(activeTask);
                              }}
                            >
                              Mark Inactive
                            </button>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                  <button
                    className="icon-btn small icon-close"
                    type="button"
                    aria-label="Close details"
                    onClick={() => setDetailsOpen(false)}
                  />
                </div>
              </div>

              <h3 className="details-title">{activeTask.title}</h3>

              <div className="details-badges">
                <span><strong>Due:</strong> {activeTask.due ? formatDateStr(activeTask.due) : '-'}</span>
                <span><strong>{activeTask.frequency || '-'}</strong></span>
                <span><strong>{activeTask.status || '-'}</strong></span>
              </div>

              <div className="details-card">
                <h4>Description</h4>
                <p>{activeTask.description || 'No description available for this task.'}</p>
              </div>

              <div className="details-section compact-fields">
                <div className="section-head">
                  <h4>QUICK INFO</h4>
                  <span>Overview</span>
                </div>

                <div className="quick-info-row">
                    <span className="quick-info-label">
                      <QuickInfoIcon type="user" />
                      Assigned To
                    </span>
                    <strong>{activeTask.assigneeName || '-'}</strong>
                  </div>

                <div className="quick-info-list">
                  <div className="quick-info-row">
                    <span className="quick-info-label">
                      <QuickInfoIcon type="calendar" />
                      Delivered
                    </span>
                    <strong>
                      {activeTask.deliveredDate
                        ? formatDateStr(activeTask.deliveredDate)
                        : 'Delivery pending'}
                    </strong>
                  </div>

                  <div className="quick-info-row">
                    <span className="quick-info-label">
                      <QuickInfoIcon type="department" />
                      Department
                    </span>
                    <strong>
                      {departments.find((d) => String(d.id) === String(activeTask.departmentId))?.dept_name ||
                        activeTask.departmentId ||
                        '-'}
                    </strong>
                  </div>

                  <div className="quick-info-row">
                    <span className="quick-info-label">
                      <QuickInfoIcon type="calendar" />
                      Planned Date
                    </span>
                    <strong>{activeTask.plannedDate ? formatDateStr(activeTask.plannedDate) : '-'}</strong>
                  </div>

                  <div className="quick-info-row">
                    <span className="quick-info-label">
                      <QuickInfoIcon type="calendar" />
                      Assigned Date
                    </span>
                    <strong>{activeTask.assignedDate ? formatDateStr(activeTask.assignedDate) : '-'}</strong>
                  </div>

                  <div className="quick-info-row">
                    <span className="quick-info-label">
                      <QuickInfoIcon type="status" />
                      Status
                    </span>
                    <strong>{activeTask.status || '-'}</strong>
                  </div>

                  <div className="quick-info-row">
                    <span className="quick-info-label">
                      <QuickInfoIcon type="frequency" />
                      Frequency
                    </span>
                    <strong>{activeTask.frequency || '-'}</strong>
                  </div>

                  {isRecurringFrequencyLabel(activeTask.frequency) && (
                    <div className="quick-info-row">
                      <span className="quick-info-label">
                        <QuickInfoIcon type="frequency" />
                        Recurring
                      </span>
                      <strong>{Number(activeTask.recurringActive) === 0 ? 'Stopped' : 'Active'}</strong>
                    </div>
                  )}
                </div>
              </div>

              <div className="details-section">
                <div className="section-head">
                  <h4>PROGRESS</h4>
                  <span>
                    {checklistDone}/{activeChecklist.length} Complete
                  </span>
                </div>

                <div className="progress-line">
                  <span
                    style={{
                      width: activeChecklist.length
                        ? `${Math.round((checklistDone / activeChecklist.length) * 100)}%`
                        : '0%',
                    }}
                  />
                </div>

                <div className="checklist">
                  {activeChecklist.map((item) => {
                    const isCompletionItem = item.text === 'Marked completed';
                    return (
                      <label key={item.text} className="checklist-item">
                        <input
                          type="checkbox"
                          checked={item.done}
                          readOnly={!isCompletionItem}
                          disabled={!isCompletionItem || loading || statusSaving}
                          onChange={() => handleChecklistToggle(item)}
                        />
                        <span>{item.text}</span>
                      </label>
                    );
                  })}
                </div>
              </div>

              <div className="next-action-card">
                <h4>Next Action</h4>
                <p>Review the task, update progress, and mark it complete when done.</p>
              </div>

              <button
                className="mark-btn"
                type="button"
                disabled={activeTask?.status === 'Completed' || loading || statusSaving}
                onClick={() => {
                  if (!activeTask || activeTask.status === 'Completed') return;
                  requestStatusChange(
                    activeTask,
                    'Completed',
                    'Mark this task as completed?'
                  );
                }}
              >
                Mark Complete
              </button>
              {isRecurringFrequencyLabel(activeTask.frequency) && (user?.role === 1 || user?.role === 2) && (
                <button
                  className="mark-btn"
                  type="button"
                  style={{
                    marginTop: '10px',
                    background: Number(activeTask.recurringActive) === 0 ? '#0f766e' : '#b45309',
                  }}
                  disabled={loading || statusSaving}
                  onClick={() => handleToggleRecurring(activeTask)}
                >
                  {Number(activeTask.recurringActive) === 0 ? 'Resume Recurring' : 'Stop Recurring'}
                </button>
              )}
            </aside>
          ) : null}
        </section>
        
        {loggingOut ? (
          <div className="workspace-loader">
            <LoadingIndicator variant="panel" label="Logging out..." />
          </div>
        ) : null}
      </main>

      {mobileMenuOpen && (
        <div className="mobile-nav-overlay" onClick={() => setMobileMenuOpen(false)}>
          <aside className="mobile-nav-sheet" onClick={(e) => e.stopPropagation()}>
            <div className="mobile-nav-head">
              <div className="mobile-nav-brand">
                <LogoMark />
              </div>

              <button
                className="icon-btn small icon-close"
                type="button"
                aria-label="Close menu"
                onClick={() => setMobileMenuOpen(false)}
              />
            </div>

            <nav className="mobile-nav-list">
              <button className="mobile-nav-item">Dashboard</button>
              <button className="mobile-nav-item active">Tasks</button>
              <button className="mobile-nav-item">Team</button>
              <button className="mobile-nav-item">Reporting</button>
              <button className="mobile-nav-item">Settings</button>
              <button className="mobile-nav-item">Support</button>
              <button className="mobile-nav-item" onClick={onLogout} disabled={loggingOut}>
                {loggingOut ? (
                  <LoadingIndicator variant="button" label="Logging out..." />
                ) : (
                  <>
                    <LogoutIcon size={16} />
                    Logout
                  </>
                )}
              </button>
            </nav>
          </aside>
        </div>
      )}

      {/* STATUS CHANGE CONFIRMATION */}
      {confirmData ? (
        <div className="modal-overlay">
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Confirm status change</h2>
            </div>
            <div style={{ padding: '0 20px 20px' }}>
              <p>{confirmData.message}</p>
            </div>
            <div className="modal-actions">
              <button
                type="button"
                className="cancel-btn"
                disabled={statusSaving}
                onClick={() => setConfirmData(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="save-btn"
                disabled={statusSaving}
                onClick={async () => {
                  await updateTaskStatus(confirmData.task, confirmData.nextStatus);
                  setConfirmData(null);
                }}
              >
                Confirm
              </button>
            </div>
          </div>
          {statusSaving && (
            <div className="loader-overlay">
              <LoadingIndicator variant="panel" label="Updating task status..." />
            </div>
          )}
        </div>
      ) : null}

      {/* DELETE TASK CONFIRMATION */}
      {deleteConfirmData ? (
        <div className="modal-overlay">
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Mark Task Inactive</h2>
            </div>
            <div style={{ padding: '0 20px 20px' }}>
              <p>Are you sure you want to hide <strong>"{deleteConfirmData.title}"</strong> from active checklist tasks?</p>
              <p style={{ fontSize: '12px', color: '#b45309', marginTop: '8px' }}>The task will remain in the database as inactive.</p>
            </div>
            <div className="modal-actions">
              <button
                type="button"
                className="cancel-btn"
                disabled={statusSaving}
                onClick={() => setDeleteConfirmData(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="save-btn"
                style={{ backgroundColor: '#dc2626' }}
                disabled={statusSaving}
                onClick={handleDeleteTask}
              >
                Mark Inactive
              </button>
            </div>
          </div>
          {statusSaving && (
            <div className="loader-overlay">
              <LoadingIndicator variant="panel" label="Marking task inactive..." />
            </div>
          )}
        </div>
      ) : null}

      {/* CREATE TASK MODAL */}
      {showCreateTask && (
        <div className="modal-overlay" onClick={() => !creatingTask && setShowCreateTask(false)}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Create Task</h2>
              <button
                type="button"
                className="modal-close"
                disabled={creatingTask}
                onClick={() => setShowCreateTask(false)}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleCreateTask} className="task-form">
              <label>
                Task Title
                <input
                  type="text"
                  name="taskTitle"
                  value={taskForm.taskTitle}
                  onChange={handleTaskFormChange}
                  placeholder="Enter task title"
                  disabled={creatingTask}
                  required
                />
              </label>

              <label>
                Task Description
                <textarea
                  name="taskDescription"
                  value={taskForm.taskDescription}
                  onChange={handleTaskFormChange}
                  placeholder="Enter task description"
                  disabled={creatingTask}
                  required
                />
              </label>

              <FancyDropdown
  label="Assigned To"
  value={taskForm.assignedTo}
  onChange={(val) => {
    const selectedEmployee = employees.find((emp) => String(emp.id) === String(val));
    setTaskForm((prev) => ({
      ...prev,
      assignedTo: val,
      department: selectedEmployee?.dept_id ? String(selectedEmployee.dept_id) : '',
    }));
  }}
  options={employees
    .filter((emp) => String(emp.status || '').trim().toLowerCase() === 'active')
    .map((emp) => ({
      value: emp.id,
      label: emp.emp_name,
    }))}
  placeholder="Select employee"
  loading={employeesLoading}
  disabled={employeesLoading || creatingTask}
  error={employeesError}
  searchable
/>

<FancyDropdown
  label="Department"
  value={taskForm.department}
  onChange={(val) => setTaskForm((prev) => ({ ...prev, department: val }))}
  options={departments.map((dept) => ({
    value: dept.id,
    label: dept.dept_name,
  }))}
  placeholder="Select department"
  loading={departmentsLoading}
  disabled={departmentsLoading || creatingTask}
  error={departmentsError}
  searchable
/>

              <div className="form-field">
  <label>Planned Date</label>
  <div style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
    {/* Date Type Selector */}
    <select
      value={taskForm.dateType || 'regular'}
      onChange={(e) => setTaskForm({
        ...taskForm,
        dateType: e.target.value,
        plannedDate: '',
        frequency: '',
        skipDays: 1,
        recurrenceEndDate: '',
        weekdayFrequency: 'one_time',
        selectedWeekdays: [],
      })}
      style={{ flex: 1, padding: '10px', borderRadius: '4px', border: '1px solid #ccc' }}
    >
      <option value="regular">Regular Date</option>
      <option value="relative">Relative Date Pattern</option>
      <option value="weekdays">Weekday Pattern</option>
      <option value="skipDays">Skip Days Pattern</option>
      <option value="dailyRange">Daily Date Range</option>
    </select>
  </div>

  {/* Regular Date Picker */}
  {['regular', 'skipDays', 'weekdays'].includes(taskForm.dateType || 'regular') && (
    <input
      type="date"
      value={taskForm.plannedDate}
      onChange={(e) => setTaskForm({ ...taskForm, plannedDate: e.target.value })}
      style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #ccc' }}
    />
  )}

  {/* Relative Date Pattern Selector */}
  {taskForm.dateType === 'relative' && (
    <select
      value={taskForm.plannedDate}
      onChange={(e) => setTaskForm({ ...taskForm, plannedDate: e.target.value })}
      style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #ccc' }}
    >
      <option value="">Select a relative date pattern</option>
      <option value="last-monday-of-month">Last Monday of Month</option>
      <option value="last-tuesday-of-month">Last Tuesday of Month</option>
      <option value="last-wednesday-of-month">Last Wednesday of Month</option>
      <option value="last-thursday-of-month">Last Thursday of Month</option>
      <option value="last-friday-of-month">Last Friday of Month</option>
      <option value="last-saturday-of-month">Last Saturday of Month</option>
      <option value="last-sunday-of-month">Last Sunday of Month</option>
      <option value="last-working-day-of-month">Last Working Day of Month (Skips Sunday)</option>
      {/* <option value="last-day-of-month">Last Day of Month</option> */}
    </select>
  )}

  {taskForm.dateType === 'skipDays' && (
    <label style={{ marginTop: '12px' }}>
      Days to Skip
      <input
        type="number"
        min="1"
        step="1"
        value={taskForm.skipDays}
        onChange={(e) => setTaskForm({ ...taskForm, skipDays: e.target.value })}
        style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #ccc' }}
      />
    </label>
  )}

  {taskForm.dateType === 'weekdays' && (
    <div style={{ display: 'grid', gap: '12px', marginTop: '12px' }}>
      <FancyDropdown
        label="Pattern Frequency"
        value={taskForm.weekdayFrequency}
        onChange={(val) => setTaskForm((prev) => ({ ...prev, weekdayFrequency: val }))}
        options={[
          { value: 'one_time', label: 'One time - selected week only' },
          { value: 'weekly', label: 'Weekly' },
          { value: 'monthly', label: 'Monthly - same week of month' },
        ]}
        placeholder="Select pattern frequency"
        disabled={creatingTask}
      />
      <div>
        <span style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '8px', color: '#334155' }}>
          Occurs on
        </span>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '8px' }}>
          {WEEKDAY_OPTIONS.map((day) => (
            <label key={day.value} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: '6px' }}>
              <input
                type="checkbox"
                checked={(taskForm.selectedWeekdays || []).includes(day.value)}
                onChange={(e) => {
                  const nextDays = e.target.checked
                    ? [...(taskForm.selectedWeekdays || []), day.value]
                    : (taskForm.selectedWeekdays || []).filter((value) => value !== day.value);
                  setTaskForm({ ...taskForm, selectedWeekdays: nextDays });
                }}
                disabled={creatingTask}
              />
              {day.label}
            </label>
          ))}
        </div>
      </div>
    </div>
  )}

  {taskForm.dateType === 'dailyRange' && (
    <div style={{ display: 'grid', gap: '12px' }}>
      <label>
        Start Date
        <input
          type="date"
          value={taskForm.plannedDate}
          onChange={(e) => setTaskForm({ ...taskForm, plannedDate: e.target.value })}
          style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #ccc' }}
        />
      </label>
      <label>
        End Date
        <input
          type="date"
          value={taskForm.recurrenceEndDate}
          onChange={(e) => setTaskForm({ ...taskForm, recurrenceEndDate: e.target.value })}
          style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #ccc' }}
        />
      </label>
    </div>
  )}
</div>

              {['regular', 'relative'].includes(taskForm.dateType || 'regular') && (
                <FancyDropdown
                label="Frequency of Task"
                value={taskForm.frequency}
                onChange={(val) => setTaskForm((prev) => ({ ...prev, frequency: val }))}
                options={[
                  { value: 'Daily', label: 'Daily' },
                  { value: 'Weekly', label: 'Weekly' },
                  { value: 'Monthly', label: 'Monthly' },
                  { value: 'Quarterly', label: 'Quarterly' },
                  { value: 'Yearly', label: 'Yearly' },
                ]}
                placeholder="Select frequency"
                disabled={creatingTask}
                required
              />
              )}

              <div className="modal-actions">
                <button
                  type="button"
                  className="cancel-btn"
                  disabled={creatingTask}
                  onClick={() => setShowCreateTask(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="save-btn" disabled={creatingTask}>
                  {creatingTask ? (
                    <div className="loader-overlay">
                      <LoadingIndicator variant="panel" label="Creating task..." />
                    </div>
                  ) : (
                    'Create Task'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* EDIT TASK MODAL */}
      {showEditTask && (
        <div className="modal-overlay" onClick={() => !creatingTask && setShowEditTask(false)}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Edit Task</h2>
              <button
                type="button"
                className="modal-close"
                disabled={creatingTask}
                onClick={() => setShowEditTask(false)}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleEditTask} className="task-form">
              <label>
                Task Title
                <input
                  type="text"
                  name="taskTitle"
                  value={editForm.taskTitle}
                  onChange={handleEditFormChange}
                  placeholder="Enter task title"
                  disabled={creatingTask}
                  required
                />
              </label>

              <label>
                Task Description
                <textarea
                  name="taskDescription"
                  value={editForm.taskDescription}
                  onChange={handleEditFormChange}
                  placeholder="Enter task description"
                  disabled={creatingTask}
                  required
                />
              </label>

              {/* Add this FancyDropdown for Assigned To */}
              <FancyDropdown
                label="Assigned To"
                value={editForm.assignedTo}
                onChange={(val) => setEditForm((prev) => ({ ...prev, assignedTo: val }))}
                options={employees.map((emp) => ({
                  value: emp.id,
                  label: emp.emp_name,
                }))}
                placeholder="Select employee"
                loading={employeesLoading}
                disabled={employeesLoading || creatingTask}
                error={employeesError}
              />

              <div className="form-field">
                <label>Planned Date</label>
                <div style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
                  <select
                    value={editForm.dateType || 'regular'}
                    onChange={(e) => setEditForm({
                      ...editForm,
                      dateType: e.target.value,
                      frequency: '',
                      skipDays: 1,
                      recurrenceEndDate: '',
                      weekdayFrequency: 'one_time',
                      selectedWeekdays: [],
                    })}
                    disabled={creatingTask}
                    style={{ flex: 1, padding: '10px', borderRadius: '4px', border: '1px solid #ccc' }}
                  >
                    <option value="regular">Regular Date</option>
                    <option value="skipDays">Skip Days Pattern</option>
                    <option value="dailyRange">Daily Date Range</option>
                  </select>
                </div>

                {['regular', 'skipDays'].includes(editForm.dateType || 'regular') && (
                  <input
                    type="date"
                    name="plannedDate"
                    value={editForm.plannedDate}
                    onChange={handleEditFormChange}
                    disabled={creatingTask}
                    required
                    style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #ccc' }}
                  />
                )}

                {editForm.dateType === 'skipDays' && (
                  <label style={{ marginTop: '12px' }}>
                    Days to Skip
                    <input
                      type="number"
                      min="1"
                      step="1"
                      value={editForm.skipDays}
                      onChange={(e) => setEditForm({ ...editForm, skipDays: e.target.value })}
                      disabled={creatingTask}
                      style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #ccc' }}
                    />
                  </label>
                )}

                {editForm.dateType === 'dailyRange' && (
                  <div style={{ display: 'grid', gap: '12px' }}>
                    <label>
                      Start Date
                      <input
                        type="date"
                        name="plannedDate"
                        value={editForm.plannedDate}
                        onChange={handleEditFormChange}
                        disabled={creatingTask}
                        required
                        style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #ccc' }}
                      />
                    </label>
                    <label>
                      End Date
                      <input
                        type="date"
                        value={editForm.recurrenceEndDate}
                        onChange={(e) => setEditForm({ ...editForm, recurrenceEndDate: e.target.value })}
                        disabled={creatingTask}
                        required
                        style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #ccc' }}
                      />
                    </label>
                  </div>
                )}
              </div>

              {editForm.dateType === 'regular' && (
                <FancyDropdown
                  label="Frequency of Task"
                  value={editForm.frequency}
                  onChange={(val) => setEditForm((prev) => ({ ...prev, frequency: val }))}
                  options={[
                    { value: 'Daily', label: 'Daily' },
                    { value: 'Weekly', label: 'Weekly' },
                    { value: 'Monthly', label: 'Monthly' },
                    { value: 'Quarterly', label: 'Quarterly' },
                    { value: 'Yearly', label: 'Yearly' },
                  ]}
                  placeholder="Select frequency"
                  disabled={creatingTask}
                />
              )}

              {/* Add this FancyDropdown for Status */}
              <FancyDropdown
                label="Task Status (Override)"
                value={editForm.taskStatus}
                onChange={(val) => setEditForm((prev) => ({ ...prev, taskStatus: val }))}
                options={[
                  { value: 'To Do', label: 'To Do' },
                  { value: 'In Progress', label: 'In Progress' },
                  { value: 'In Review', label: 'In Review' },
                  { value: 'Completed', label: 'Completed' },
                ]}
                placeholder="Select status"
                disabled={creatingTask}
              />

              {/* Add this FancyDropdown for Active Status */}
              <FancyDropdown
                label="Task Active Status"
                value={editForm.active}
                onChange={(val) => setEditForm((prev) => ({ ...prev, active: Number(val) }))}
                options={[
                  { value: 1, label: 'Active' },
                  { value: 0, label: 'Inactive' },
                ]}
                placeholder="Select status"
                disabled={creatingTask}
              />

              <div className="modal-actions">
                <button
                  type="button"
                  className="cancel-btn"
                  disabled={creatingTask}
                  onClick={() => setShowEditTask(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="save-btn" disabled={creatingTask}>
                  {creatingTask ? (
                    <div className="loader-overlay">
                      <LoadingIndicator variant="panel" label="Updating task..." />
                    </div>
                  ) : (
                    'Save Changes'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* BULK DELETE CONFIRMATION */}
      {showBulkDeleteConfirm && (
        <div className="modal-overlay">
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Mark Multiple Tasks Inactive</h2>
            </div>
            <div style={{ padding: '0 20px 20px' }}>
              <p>Are you sure you want to hide <strong>{selectedTaskIds.length} tasks</strong> from active checklist tasks?</p>
              <p style={{ fontSize: '12px', color: '#b45309', marginTop: '8px' }}>The selected tasks will remain in the database as inactive.</p>
            </div>
            <div className="modal-actions">
              <button
                type="button"
                className="cancel-btn"
                disabled={statusSaving}
                onClick={() => setShowBulkDeleteConfirm(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="save-btn"
                style={{ backgroundColor: '#dc2626' }}
                disabled={statusSaving}
                onClick={handleBulkDelete}
              >
                Mark Inactive
              </button>
            </div>
          </div>
          {statusSaving && (
            <div className="loader-overlay">
              <LoadingIndicator variant="panel" label="Marking tasks inactive..." />
            </div>
          )}
        </div>
      )}

      {/* BULK EDIT MODAL */}
      {showBulkEditModal && (
        <div className="modal-overlay" onClick={() => !statusSaving && setShowBulkEditModal(false)}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Bulk Edit {selectedTaskIds.length} Tasks</h2>
              <button
                type="button"
                className="modal-close"
                disabled={statusSaving}
                onClick={() => setShowBulkEditModal(false)}
              >
                ×
              </button>
            </div>

            <form className="task-form">
              <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '8px' }}>
                Leave a field as "Unchanged" to keep its current value across all selected tasks.
              </p>

              <FancyDropdown
                label="New Assigned To"
                value={bulkEditForm.assignedTo}
                onChange={(val) => setBulkEditForm((prev) => ({ ...prev, assignedTo: val }))}
                options={employees.map((emp) => ({
                  value: emp.id,
                  label: emp.emp_name,
                }))}
                placeholder="Unchanged (Keep current)"
                loading={employeesLoading}
                disabled={employeesLoading || statusSaving}
              />

              <FancyDropdown
                label="New Task Status"
                value={bulkEditForm.taskStatus}
                onChange={(val) => setBulkEditForm((prev) => ({ ...prev, taskStatus: val }))}
                options={[
                  { value: 'To Do', label: 'To Do' },
                  { value: 'In Progress', label: 'In Progress' },
                  { value: 'In Review', label: 'In Review' },
                  { value: 'Completed', label: 'Completed' },
                ]}
                placeholder="Unchanged (Keep current)"
                disabled={statusSaving}
              />

              <div className="modal-actions" style={{ marginTop: '16px' }}>
                <button
                  type="button"
                  className="cancel-btn"
                  disabled={statusSaving}
                  onClick={() => setShowBulkEditModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="save-btn"
                  disabled={statusSaving}
                  onClick={handleBulkEdit}
                >
                  {statusSaving ? 'Updating...' : 'Update All'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
       <Toaster position="top-right" />
    </div>
  );
}

export default TaskList;
